import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@hugeicons-pro/core-stroke-rounded';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { ContextSheet, IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { ActionList } from '@/components/ui/ActionList';
import { SheetScrollView } from '@/components/ui/SheetScrollView';
import { useCommunity } from '@/hooks/useCommunity';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { SUPPORT_URLS } from '@/constants/urls';
import { ReportReason, ReportSubject } from '@/services/domain/SupportService';

const REASON_LABELS: Record<ReportReason, string> = {
  [ReportReason.Scam]: 'report.reasons.scam',
  [ReportReason.Spam]: 'report.reasons.spam',
  [ReportReason.FalsePortrayal]: 'report.reasons.falsePortrayal',
  [ReportReason.Harassment]: 'report.reasons.harassment',
  [ReportReason.Intolerance]: 'report.reasons.intolerance',
  [ReportReason.Trolling]: 'report.reasons.trolling',
  [ReportReason.Disparagement]: 'report.reasons.disparagement',
  [ReportReason.DonationRequest]: 'report.reasons.donationRequest',
  [ReportReason.PromotionalPosting]: 'report.reasons.promotionalPosting',
  [ReportReason.PhotoPolicy]: 'report.reasons.photoPolicy',
  [ReportReason.UncreditedArtwork]: 'report.reasons.uncreditedArtwork',
  [ReportReason.Underage]: 'report.reasons.underage',
  [ReportReason.IllegalActivity]: 'report.reasons.illegalActivity',
  [ReportReason.Other]: 'report.reasons.other',
};

const ALL_REASONS = Object.values(ReportReason).filter((value): value is ReportReason => typeof value === 'number');

const REASONS_BY_SUBJECT: Record<ReportSubject, ReportReason[]> = {
  member: ALL_REASONS,
  post: [
    ReportReason.Scam,
    ReportReason.Spam,
    ReportReason.Harassment,
    ReportReason.Intolerance,
    ReportReason.Trolling,
    ReportReason.Disparagement,
    ReportReason.DonationRequest,
    ReportReason.PromotionalPosting,
    ReportReason.IllegalActivity,
    ReportReason.Other,
  ],
  comment: [
    ReportReason.Scam,
    ReportReason.Spam,
    ReportReason.Harassment,
    ReportReason.Intolerance,
    ReportReason.Trolling,
    ReportReason.Disparagement,
    ReportReason.DonationRequest,
    ReportReason.PromotionalPosting,
    ReportReason.IllegalActivity,
    ReportReason.Other,
  ],
  photo: [
    ReportReason.Spam,
    ReportReason.FalsePortrayal,
    ReportReason.Harassment,
    ReportReason.Intolerance,
    ReportReason.DonationRequest,
    ReportReason.PromotionalPosting,
    ReportReason.PhotoPolicy,
    ReportReason.UncreditedArtwork,
    ReportReason.Underage,
    ReportReason.IllegalActivity,
    ReportReason.Other,
  ],
  conversation: ALL_REASONS.filter((reason) => reason !== ReportReason.UncreditedArtwork),
};

interface IReportReasonPayload {
  subject: ReportSubject;
  onReasonSelected: (reason: ReportReason) => void;
}

function ReportReasonList({ onClose, payload }: IContextSheetContentProps) {
  const { t } = useTranslation();
  const community = useCommunity();
  const openWebPage = useOpenWebPage();
  const { subject, onReasonSelected } = payload as IReportReasonPayload;

  const options = REASONS_BY_SUBJECT[subject]
    .filter((reason) => reason !== ReportReason.UncreditedArtwork || community?.code === 'ferzu')
    .map((reason) => ({ key: reason, label: t(REASON_LABELS[reason]), value: reason }));

  // Report synchronously from the tap: on web the portal tab must open inside the user gesture
  const handleSelect = (reason: ReportReason) => {
    onReasonSelected(reason);
    onClose?.();
  };

  return (
    <VStack className="min-h-0 flex-1">
      <Pressable
        onPress={() => openWebPage(SUPPORT_URLS.atlassian.legal.communityRules)}
        accessibilityRole="link"
        className="mb-2 self-start rounded-md px-4 py-2">
        <HStack className="items-center gap-2">
          <HugeIconsWrapper icon={InformationCircleIcon} size={18} className="text-primary-600" />
          <Text size="sm" className="text-primary-600">
            {t('report.rulesLink')}
          </Text>
        </HStack>
      </Pressable>
      <SheetScrollView>
        <ActionList options={options} onSelect={handleSelect} />
      </SheetScrollView>
    </VStack>
  );
}

ReportReasonList.sheetConfig = {
  height: 'lg',
  additionalSnapPoints: ['90%'],
  scrollableContent: true,
  integratedNativeSheetScrollGestures: true,
} satisfies ISheetConfig;

interface IReportReasonSheetProps {
  isOpen: boolean;
  onClose: () => void;
  subject: ReportSubject;
  onReasonSelected: (reason: ReportReason) => void;
}

/**
 * Asks the member why they are reporting before the report is recorded and the support form opens
 */
export function ReportReasonSheet({ isOpen, onClose, subject, onReasonSelected }: IReportReasonSheetProps) {
  const { t } = useTranslation();

  return (
    <ContextSheet
      isOpen={isOpen}
      onClose={onClose}
      contentComponent={ReportReasonList}
      title={t('report.reasonTitle')}
      payload={{ subject, onReasonSelected }}
    />
  );
}
